import { createContext, useContext } from 'react';
import { Html } from '@react-three/drei';

// Globalus "Rodyti gylius užvedus pelę" perjungiklis (vartotojas: "Sukurk mygtuka
// rodyti/nerodyti baldu gyliu on hover") — PanelDepthTooltip naudojamas DEŠIMTYSE
// atskirų komponentų (CabinetPanel, DividerDragHandle, NarySectionDivider,
// FrontTrimBox, TopBox, OuterDoors, ShelfWithRails...), tad perduoti NAUJĄ prop'ą
// per VISUS juos reikštų keisti kiekvieną iš jų atskirai (ta pati rizika, kaip
// kiti šios sesijos "pataisyta vienoje vietoje, pamiršta kitoje" radiniai — žr.
// [[project_merged_door_facade_extend_fix_2026_10]]). Vietoj to, PATS
// PanelDepthTooltip skaito šią Context reikšmę — Builder3D.jsx tiesiog apgaubia
// <Canvas> turinį <DepthHoverContext.Provider>, visiškai nekeisdamas jokio
// tarpinio komponento. Numatytoji (be Provider) reikšmė — true, t.y. SENAS,
// visada-įjungtas elgesys išlieka saugus atsarginis variantas.
export const DepthHoverContext = createContext(true);

// Bendras "tik krašto hover" mechanizmas VISOMS baldo plokštėms (karkaso plokštės,
// pertvaros, Pagrindo priekinė juosta/dėžės) — vartotojas: "labai sudetinga bendrai ant
// baldo ploksciu uzejus pelyte jas pazymeti... ar galima padaryti taip, kad uzejus pelyte
// ant KRASTINES pacios plokstes ji pasizymetu zalia spalva (nereikia tada zymeti melynai
// visos plokstes) ir paspaudus atsirastu tie mygtukai... realiai turi tik plokstes
// krastine tureti hover efekta ir peles paspaudimas veikti tik paspaudus ant jos".
//
// Anksčiau KIEKVIENA plokštė (CabinetPanel/DividerDragHandle/NarySectionDivider/
// FrontTrimBox) hover/click zona buvo VISAS matomas paviršius, o vizualinis atsakas —
// emissive mėlynas atspalvis ant VISO paviršiaus. Dabar: hover/click galioja TIK arti
// briaunos (žr. EDGE_MARGIN_M), o vizualinis atsakas — plonas ŽALIAS rėmelis aplink
// plokštę (žr. HoverEdgeFrame), ne viso paviršiaus atspalvis.
//
// Kodėl UV, ne event.point: Three.js pointer event'as ant BoxGeometry face'o suteikia
// `.uv` (0..1 kiekvienam matomam paviršiui, NEPRIKLAUSOMAI nuo dėžės Z gylio) — tad ta
// pati formulė vienodai veikia ir tikram matomam paviršiui, IR papildomam, giliau
// ištemptam nematomam "hit" Box'ui (žr. FrontTrimBox/DividerDragHandle komentarus apie
// kampinės kameros paspaudimo konkurenciją) — abu naudoja TĄ PATĮ args[0]/args[1]
// (plotį/aukštį), tik skirtingą gylį, tad UV→metrai konversija abiem sutampa.
export const EDGE_MARGIN_M = 0.03;

// Ploniems elementams (pvz. Pagrindo priekinė juosta, ~0.1m aukščio) fiksuotas 3cm
// kraštas apimtų >50% viso aukščio — "krašto" hover beveik visada įsijungtų, kad ir
// kur ant plokštės užeitum (vartotojas: "dabar net uzejus ant plokstes pasizymi
// briauna zaliai, o turetu zymetis TIK kai uzeinu pelyte ant pacios briaunos"). Šis
// koeficientas apkarpo kraštą iki DALIES atitinkamo matmens, kad plonai/siaurai
// plokštei kraštas liktų plonas, o ne praktiškai visas paviršius — žr.
// isNearPanelEdgeWorld, kur taikomas PER AŠĮ (width/height savo ribą skaičiuoja
// atskirai, nes viena ašis gali būti didelė, o kita — plona).
export const EDGE_MARGIN_RATIO = 0.15;

// Hysteresis ("lipni") zona: kai briauna JAU aktyvi (žalia), leidžiama likti aktyviai
// (ir PASPAUSTI) šiek tiek PLAČIAU, ne vien tiksliai tame pačiame siaurame ruože, kuris
// ją pirmą kartą uždegė — vartotojas: "uzvedu pelyte ant briaunu jos pasidaro zalios,
// bet labai sudetinga paspausti... kazkokia papildoma atsargine vieta pelytei paspausti
// ir ji nesiaktyvuotu kol nera pataikoma tiksliai ant briaunos... tai butu zymiai
// lengviau paspausti... ir tai netrugdytu paspausti ant mazu skyriu". ĮĖJIMO riba (kada
// briauna PIRMĄ KARTĄ užsidega žalia) lieka siaura/tiksli (EDGE_MARGIN_M/_RATIO), bet
// IŠĖJIMO riba (kol ji vėl užgęsta) platesnė — klasikinis hysteresis/"sticky target"
// sprendimas: pelė tarp onPointerMove atnaujinimo ir paties click įvykio beveik visada
// šiek tiek pasislenka, o onClick žemiau (CabinetPanel/FrontTrimBox) tikrina PASKUTINĮ
// onPointerMove rezultatą (edgeHoveredRef), ne perskaičiuoja iš naujo — tad vien šis
// platesnis IŠĖJIMO slenkstis automatiškai padaro click žymiai patikimesnį, visiškai
// nekeičiant pačios click logikos. Platesnė, bet NE visa plokštė — vis tiek palieka
// saugią juostą ties pačiu plokštės VIDURIU, kad gretimos smulkios sekcijos liktų
// paspaudžiamos (žr. isNearStripCenter pertvaroms — ten TA PATI logika, atvirkščiai).
export const EDGE_MARGIN_M_STICKY = 0.07;
export const EDGE_MARGIN_RATIO_STICKY = 0.35;

// true, jei event'o UV taškas (paverstas į metrus pagal width/height) yra arčiau nei
// `margin` bent vienos iš keturių plokštės briaunų. Grąžina TRUE (leidžia veikti), jei
// event'as neturi .uv (pvz. ne-Box geometrija) — saugus atsarginis kelias, kad
// niekas netyčia netaptų visiškai nepaspaudžiama vien dėl trūkstamo UV.
// `sticky` — žr. EDGE_MARGIN_M_STICKY komentarą aukščiau; perduoti TRUE, kai ši
// briauna ŠIUO METU (prieš šį skaičiavimą) jau buvo laikoma aktyvia.
export function isNearPanelEdge(e, width, height, margin, sticky = false) {
  const uv = e?.uv;
  if (!uv) return true;
  // PLONOMS plokštėms (pvz. CabinetPanel "top"/"bottom" karkaso skydai, ~1.8cm storio
  // kitoje ašyje, arba ShelfWithRails ~6cm aukščio lentyna) fiksuotas EDGE_MARGIN_M
  // (3cm) PO VIENĄ AŠĮ apima VISĄ tos ašies ilgį (2*3cm=6cm >= plokštės storis), tad
  // `y <= margin || y >= height - margin` tampa TIESA kiekvienam y — visas paviršius,
  // o ne tik briauna, "žalias". Tiksliai ta pati problema, kuriai spręsti jau buvo
  // sukurtas EDGE_MARGIN_RATIO (žr. isNearPanelEdgeWorld žemiau) — bet jis anksčiau
  // buvo pritaikytas TIK tai, pasaulio-koordinatėmis paremtai funkcijai, ne šitai
  // (UV-based), nors abi sprendžia TĄ PATĮ "ar arti briaunos" klausimą. Vartotojas:
  // "Zalia hover spalva turi pasirodyti TIK kai pelyte uzeinu ant plokstes briaunos o
  // ne pacios plokstes" — ta pati, anksčiau jau spręsta problema, čia buvo likusi
  // nepataisyta.
  const capM = sticky ? EDGE_MARGIN_M_STICKY : EDGE_MARGIN_M;
  const ratio = sticky ? EDGE_MARGIN_RATIO_STICKY : EDGE_MARGIN_RATIO;
  const marginX = margin ?? Math.min(capM, width * ratio);
  const marginY = margin ?? Math.min(capM, height * ratio);
  const x = uv.x * width;
  const y = uv.y * height;
  return x <= marginX || x >= width - marginX || y <= marginY || y >= height - marginY;
}

// Ta pati "arti briaunos" patikra, bet skirta plokštėms, kurių papildomas, giliau/
// plačiau/aukščiau ištemptas nematomas "hit" Box'as TURI KITOKIUS args[0]/args[1] (ne
// tik kitą gylį) nei pati matoma plokštė — žr. UnifiedBase.jsx FrontTrimBox hitWidthX/
// hitHeightArg. Tokiu atveju e.uv (0..1 PAGAL TO KONKREČIO Box'o, į kurį pataikyta,
// matmenis) nebeatitinka vienodos UV→metrai formulės abiem dėžėms (žr. isNearPanelEdge
// komentarą aukščiau — ten tas sutapimas veikia TIK kai hit-box'as skiriasi VIEN gyliu).
// Čia naudojamas e.point (PASAULIO koordinatės) + obj.parent.worldToLocal — NE paprastas
// atėmimas iš `position` prop'o (tas jau buvo BANDYTA ir KLAIDINGA: `position` yra
// VIETINĖ koordinatė protėvio group'o atžvilgiu, o e.point — PASAULIO; jos sutampa TIK
// kai nėra jokios protėvio transformacijos, kas čia netiesa — FurnitureGroup turi savo
// poziciją). worldToLocal atlieka TIKRĄ, korektišką konversiją per VISĄ protėvių grandinę,
// nepriklausomai nuo to, kurį konkretų Box'ą (matomą ar hit) spindulys pagavo — abu yra
// TIESIOGINIAI to paties <group position={position}> vaikai, tad e.eventObject.parent
// visada yra ta pati grupė, kurios vietinėse koordinatėse matoma plokštė stovi [0,0,*].
export function isNearPanelEdgeWorld(e, width, height, margin, sticky = false) {
  const pt = e?.point;
  const obj = e?.eventObject || e?.object;
  if (!pt || !obj || !obj.parent) return true;
  const local = obj.parent.worldToLocal(pt.clone());
  const capM = sticky ? EDGE_MARGIN_M_STICKY : EDGE_MARGIN_M;
  const ratio = sticky ? EDGE_MARGIN_RATIO_STICKY : EDGE_MARGIN_RATIO;
  const marginX = margin ?? Math.min(capM, width * ratio);
  const marginY = margin ?? Math.min(capM, height * ratio);
  return Math.abs(local.x) >= width / 2 - marginX || Math.abs(local.y) >= height / 2 - marginY;
}

// Pertvaros (DividerDragHandle/NarySectionDivider) turi TYČIA platesnę nematomą
// "hit" zoną už pačią plokštę (lengviau pataikyti pele), tad joms "arti briaunos"
// (isNearPanelEdge, t.y. arti PLATAUS hit-box'o PERIMETRO) netinka — plati hit
// zona IR YRA briaunos "pagalvė", o plona pertvara stovi tiksliai jos VIDURYJE.
// Čia tikrinama atvirkščiai: ar taškas arti PLATAUS hit-box'o CENTRO (ties kuriuo
// realiai stovi plona pertvara) — vartotojas: "vilkimas turi būti apribotas iki
// briaunos, jeigu užeinu pele hover ant briaunos tada turiu galimybę arba vilkt,
// arba paspaust ant jos" (86cay2ygr apraše "Pertvarų/Lentynų vs. skyriaus
// žymėjimas" — kaimyninė RecursiveSection leaf sekcija savo click-box'ą tyčia
// ištempia giliau (depth+0.07, žr. ten komentarą), tad be šio apribojimo
// kaimyninė sekcija raycast varžybas laimėdavo per VISĄ platų pertvaros hit-box'ą,
// išskyrus siaurą tikrą tarpelį — pertvarą pažymėti/vilkti tapdavo sunku).
// `uvCoord` — JAU IŠRINKTAS e.uv.x ARBA e.uv.y komponentas (kurį naudoti, priklauso
// nuo pertvaros orientacijos — žr. panaudojimą), ne visas `.uv` objektas.
export function isNearStripCenter(uvCoord, axisSize, margin = EDGE_MARGIN_M) {
  if (uvCoord == null) return true;
  const posInMeters = uvCoord * axisSize;
  return Math.abs(posInMeters - axisSize / 2) <= margin;
}

// Plonas rėmelis (4 siauros plokštumėlės, ne LineLoop) aplink plokštės perimetrą —
// LineLoop (žr. PanelEdges) realiame WebGL beveik visada piešiamas TIK 1px storio
// (linewidth ignoruojamas daugumoje naršyklių), tad hover/select atsakui, kuris TURI
// būti aiškiai pastebimas, per silpnas. Šios 4 plokštumėlės gali turėti tikrą,
// reguliuojamą storį (frameThickness), todėl rėmelis realiai matomas.
//
// Spalva = tiksliai tas pats atspalvis, kaip esamas projekto prekės ženklo
// "colorBrandBackground" (žr. theme/brand.js fititBrandRamp shade80, #09aa89) — TAS
// PATS hex jau naudojamas šiame faile OverlayBtn "isFacade" aktyvios piktogramos
// spalvai. Vartotojas: "pakeisk zalia spalva i default kuri visur naudojama siame
// projekte" — anksčiau čia buvo bendrinis Tailwind žalias (#22c55e), nesutampantis
// su niekur kitur projekte naudojama spalva.
export const PANEL_HOVER_COLOR = '#09aa89';

export const HoverEdgeFrame = ({ width, height, depth, color = PANEL_HOVER_COLOR, thickness = 0.012, visible = true }) => {
  if (!visible) return null;
  const z = depth / 2 + 0.002;
  return (
    <group renderOrder={999}>
      <mesh position={[0, height / 2 - thickness / 2, z]}>
        <planeGeometry args={[width, thickness]} />
        <meshBasicMaterial color={color} toneMapped={false} depthTest={false} />
      </mesh>
      <mesh position={[0, -height / 2 + thickness / 2, z]}>
        <planeGeometry args={[width, thickness]} />
        <meshBasicMaterial color={color} toneMapped={false} depthTest={false} />
      </mesh>
      <mesh position={[-width / 2 + thickness / 2, 0, z]}>
        <planeGeometry args={[thickness, height]} />
        <meshBasicMaterial color={color} toneMapped={false} depthTest={false} />
      </mesh>
      <mesh position={[width / 2 - thickness / 2, 0, z]}>
        <planeGeometry args={[thickness, height]} />
        <meshBasicMaterial color={color} toneMapped={false} depthTest={false} />
      </mesh>
    </group>
  );
};

// Bendras "plokštės gylis" tooltip'as — vartotojas: "Sukurk 3D vaizde tooltip kuris
// butu parodomas visada pelyte uzejus (on-hover) ant bet kokios baldo plokstes ir
// parodytu tos plokstes gyli." TYČIA nepriklauso nuo isNearPanelEdge/isHoverable
// (activeStep interaktyvumo) — tai grynai informacinis, nepaspaudžiamas elementas,
// tad veikia ant VISO plokštės paviršiaus ir bet kuriame žingsnyje, skirtingai nuo
// click/drag skirto hover'o aukščiau. `depth` metrais (konvertuojama į cm čia).
export const PanelDepthTooltip = ({ depth, position, visible }) => {
  // useContext PRIEŠ "if (!visible) return" — React Hook'ai niekada negali būti
  // už sąlyginio return'o, nepriklausomai nuo to, kiek anksti jis stovi.
  const showDepthOnHover = useContext(DepthHoverContext);
  if (!visible || !showDepthOnHover) return null;
  return (
    <Html position={position} center style={{ pointerEvents: 'none' }} zIndexRange={[500, 0]}>
      <div style={{
        background: '#111', color: '#fff', padding: '3px 8px', borderRadius: 5,
        fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap', fontFamily: 'sans-serif',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}>
        Gylis: {(depth * 100).toFixed(1)} cm
      </div>
    </Html>
  );
};
